// src/pages/GenderHome.jsx — v2
// CHANGED FROM v1 (1 Aug 2026): passes priority={true} to the first 4
// cards — real LCP fix, see ProductCard.jsx v25.
// NEW FILE. Shared component for both /women and /men routes (rendered
// with a different `gender` prop from App.jsx, same as Home.jsx's overall
// structure but with a gender-specific rotating "Did you know" hero
// instead of the product-slide hero, and every section filtered to
// product.gender === gender. Categories are pulled live from the Sheet's
// existing Gender/Primary Folder data via useProducts.js — not hardcoded.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';
import './Home.css';

const DID_YOU_KNOW_TIPS = [
  "Did you know? Every product here is private label — no brand markup, ever.",
  "Did you know? You can Share & Earn on every product and keep your own margin.",
  "Did you know? We deliver pan-India, no matter where you are.",
  "Did you know? Your nb wallet cashback rate increases automatically as you shop more.",
  "Did you know? All items are quality-checked before shipping.",
  "Did you know? WhatsApp support is available if you need help with sizing or anything else.",
];

const CATEGORY_META = {
  Garments: { icon: 'ti-shirt', color: 'linear-gradient(135deg, #1B2A4A 0%, #2C4070 100%)' },
  Jewellery: { icon: 'ti-diamond', color: 'linear-gradient(135deg, #3D2B0A 0%, #7A5520 100%)' },
  Watches: { icon: 'ti-clock', color: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' },
  Bags: { icon: 'ti-briefcase', color: 'linear-gradient(135deg, #2d1b69 0%, #553c9a 100%)' },
  Shoes: { icon: 'ti-shoe', color: 'linear-gradient(135deg, #1a3a2a 0%, #2d6a4f 100%)' },
};

export default function GenderHome({ gender }) {
  const navigate = useNavigate();
  const { products, loading } = useProducts();
  const [tipIndex, setTipIndex] = useState(0);

  const genderProducts = products.filter(p => p.gender === gender);
  const newArrivals = genderProducts.filter(p => p.tag === 'new').slice(0, 8);
  const displayProducts = newArrivals.length > 0 ? newArrivals : genderProducts.slice(0, 8);

  // categories present within this gender's products, live from the Sheet
  const categoriesPresent = [...new Set(genderProducts.map(p => p.category))]
    .filter(cat => CATEGORY_META[cat]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTipIndex(i => (i + 1) % DID_YOU_KNOW_TIPS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  function goToCategory(cat) {
    sessionStorage.setItem('lastFilter', cat.toLowerCase());
    navigate(`/shop?cat=${cat.toLowerCase()}`);
  }

  return (
    <div className="app-shell">
      <Navbar />

      {/* Rotating "Did you know" hero */}
      <div className="gh-hero">
        <div className="gh-hero-label">{gender}'s edit</div>
        <p key={tipIndex} className="gh-hero-tip">{DID_YOU_KNOW_TIPS[tipIndex]}</p>
        <button className="gh-hero-btn" onClick={() => navigate(`/shop?cat=${gender === 'Women' ? 'garments' : 'garments'}`)}>
          Shop now
        </button>
      </div>

      {/* Categories present for this gender */}
      {categoriesPresent.length > 0 && (
        <div className="page-body" style={{ paddingBottom: 0 }}>
          <div className="section-header">
            <span className="section-title">Shop by category</span>
          </div>
          <div className="cat-scroll">
            {categoriesPresent.map(cat => {
              const meta = CATEGORY_META[cat];
              const count = genderProducts.filter(p => p.category === cat).length;
              return (
                <div key={cat} className="cat-scroll-card" onClick={() => goToCategory(cat)} style={{ background: meta.color }}>
                  <i className={`ti ${meta.icon} cat-scroll-icon`}></i>
                  <div className="cat-scroll-label">
                    <div className="cat-scroll-name">{cat}</div>
                    <div className="cat-scroll-count">{count}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Products for this gender */}
      <div className="page-body" style={{ paddingTop: 20 }}>
        <div className="section-header">
          <span className="section-title">
            {newArrivals.length > 0 ? `✦ New in ${gender}'s` : `${gender}'s picks`}
          </span>
          <button className="view-all-pill" onClick={() => navigate(`/shop`)}>View all <i className="ti ti-arrow-right"></i></button>
        </div>
        {loading ? (
          <div className="loading-grid">
            {[...Array(4)].map((_, i) => <div key={i} className="prod-skeleton"></div>)}
          </div>
        ) : displayProducts.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>No {gender.toLowerCase()}'s items live yet — check back soon.</div>
          </div>
        ) : (
          <div className="prod-grid">
            {displayProducts.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 4} />)}
          </div>
        )}
      </div>

      <Footer />
      <BottomNav />

      <style>{`
        .gh-hero {
          background: linear-gradient(135deg, #1B2A4A 0%, #2C4070 100%);
          padding: 32px 20px;
          text-align: center;
          color: #fff;
        }
        .gh-hero-label {
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #D4A020;
          margin-bottom: 10px;
        }
        .gh-hero-tip {
          font-size: 14px;
          line-height: 1.6;
          max-width: 320px;
          margin: 0 auto 16px;
          min-height: 44px;
          animation: ghTipFade 0.4s ease;
        }
        @keyframes ghTipFade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .gh-hero-btn {
          background: #fff;
          color: var(--navy);
          border: none;
          border-radius: 6px;
          padding: 10px 24px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          font-family: var(--font);
        }
        .view-all-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--navy, #1B2A4A);
          color: #fff;
          border: none;
          border-radius: 999px;
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          font-family: var(--font);
        }
        .view-all-pill i {
          font-size: 13px;
        }
      `}</style>
    </div>
  );
}